import {
  addOptionalNullableToSchema,
  dementiaDiagnosisRegex,
  optionalNullableNameSchema,
  requiredErrorMessage,
} from '@common/schema';
import { t } from 'i18next';
import * as yup from 'yup';
import { MAXIMUM_TEXT_AREA_LENGTH } from '@common/types/constants.ts';
import { OTHER_SPECIFY_VALUE } from '../constants';

export const carePlanSchema = yup.object().shape({
  chronicConditions: yup.array().of(
    yup.object({
      condition: yup
        .string()
        .max(
          MAXIMUM_TEXT_AREA_LENGTH,
          `This input must be ${MAXIMUM_TEXT_AREA_LENGTH} characters or less. Please reduce the length to proceed.`,
        ),
      recommendations: yup
        .string()
        .max(
          MAXIMUM_TEXT_AREA_LENGTH,
          `This input must be ${MAXIMUM_TEXT_AREA_LENGTH} characters or less. Please reduce the length to proceed.`,
        ),
      otherConditionText: yup
        .string()
        .max(
          MAXIMUM_TEXT_AREA_LENGTH,
          `This input must be ${MAXIMUM_TEXT_AREA_LENGTH} characters or less. Please reduce the length to proceed.`,
        )
        .when('condition', ([condition], schema) =>
          condition === OTHER_SPECIFY_VALUE
            ? schema.trim().required(requiredErrorMessage)
            : schema,
        ),
    }),
  ),
  chronic_conditions_icd10: yup.array().of(optionalNullableNameSchema),
  maritalStatus: addOptionalNullableToSchema(
    yup
      .string()
      .oneOf([
        'Single',
        'Married',
        'Divorced',
        'Widowed',
        'Separated',
        'Domestic Partnership',
        'Unknown',
        '',
      ]),
  ),
  fastScore: addOptionalNullableToSchema(
    yup
      .string()
      .oneOf([
        '7f - Severe Dementia, Can no longer hold head up',
        '7e - Severe Dementia, Can no longer smile',
        '7d - Severe Dementia, Can no longer sit up w/o assistance',
        '7c - Severe Dementia, Can no longer walk',
        '7b - Severe Dementia, Speaks only 1 word clearly',
        '7a - Severe Dementia, Speaks 5-6 words during days',
        '6e - Moderately Severe Dementia, Fecal incontinence',
        '6d - Moderately Severe Dementia, Urinary incontinence',
        '6c - Moderately Severe Dementia, Needs help toileting',
        '6b - Moderately Severe Dementia, Needs help bathing',
        '6a - Moderately Severe Dementia, Needs help putting on clothing',
        '5 - Moderate Dementia, Needs help selecting proper attire',
        '4 - Mild Dementia, IADLs impacted',
        '3 - Mild Cognitive Impairment',
        '2 - Possible Mild Cognitive Impairment',
        '1 - No Cognitive Decline, Normal function',
        '',
      ]),
  ),
  dementiaType: addOptionalNullableToSchema(
    yup
      .string()
      .oneOf([
        "Alzheimer's disease (early stage)",
        "Alzheimer's disease (moderate stage)",
        "Alzheimer's disease (late stage)",
        'Vascular dementia',
        'Lewy Body dementia',
        'Frontotemporal dementia',
        "Parkinson's disease dementia",
        'Mild cognitive impairment (MCI)',
        'Unsure/Not formally diagnosed',
        '',
      ]),
  ),
  dementiaDiagnosis: addOptionalNullableToSchema(
    yup
      .string()
      .matches(
        dementiaDiagnosisRegex,
        'Up to 8-character alphanumeric ICD-10 code including "."',
      )
      .min(3, 'Dementia diagnosis must be at least 3 characters')
      .max(8, 'Dementia diagnosis must be at most 8 characters'),
  ),
  hasSteadyAndSafeLivingSituation: yup.boolean(),
  hasAccessToMeals: yup.boolean(),
  hasReliableTransportation: yup.boolean(),
  hasReliableUtilities: yup.boolean(),
  isSafeFromViolenceAndAbuse: yup.boolean(),
  healthRelatedSocialNeedsNote: yup
    .string()
    .max(
      MAXIMUM_TEXT_AREA_LENGTH,
      `This input must be ${MAXIMUM_TEXT_AREA_LENGTH} characters or less. Please reduce the length to proceed.`,
    ),
  health_care_goals: yup.array().of(
    yup.object({
      goal: yup
        .string()
        .max(
          MAXIMUM_TEXT_AREA_LENGTH,
          `This input must be ${MAXIMUM_TEXT_AREA_LENGTH} characters or less. Please reduce the length to proceed.`,
        )
        .defined(),
    }),
  ),
  recommendation: yup
    .string()
    .max(
      MAXIMUM_TEXT_AREA_LENGTH,
      `This input must be ${MAXIMUM_TEXT_AREA_LENGTH} characters or less. Please reduce the length to proceed.`,
    ),
  notes: yup
    .string()
    .max(
      MAXIMUM_TEXT_AREA_LENGTH,
      `This input must be ${MAXIMUM_TEXT_AREA_LENGTH} characters or less. Please reduce the length to proceed.`,
    ),
  physical: yup
    .string()
    .max(
      MAXIMUM_TEXT_AREA_LENGTH,
      `This input must be ${MAXIMUM_TEXT_AREA_LENGTH} characters or less. Please reduce the length to proceed.`,
    ),
  medical: yup
    .string()
    .max(
      MAXIMUM_TEXT_AREA_LENGTH,
      `This input must be ${MAXIMUM_TEXT_AREA_LENGTH} characters or less. Please reduce the length to proceed.`,
    ),
  psychological: yup
    .string()
    .max(
      MAXIMUM_TEXT_AREA_LENGTH,
      `This input must be ${MAXIMUM_TEXT_AREA_LENGTH} characters or less. Please reduce the length to proceed.`,
    ),
  other: yup
    .string()
    .max(
      MAXIMUM_TEXT_AREA_LENGTH,
      `This input must be ${MAXIMUM_TEXT_AREA_LENGTH} characters or less. Please reduce the length to proceed.`,
    ),
  acp_date: yup.date().typeError(t('validationSchemas.invalidDate')),
  home_assessment: yup
    .string()
    .oneOf(
      [
        'no_health_or_safety_concerns_noted',
        'home_assessment_concerns_noted',
        'home_assessment_concerns_not_completed',
      ],
      'At least one option must be selected.',
    ),
  home_assessment_date: yup
    .date()
    .typeError(t('validationSchemas.invalidDate'))
    .when('home_assessment', ([home_assessment], schema) =>
      home_assessment !== 'home_assessment_concerns_not_completed'
        ? schema.required(requiredErrorMessage)
        : schema.optional().nullable(),
    ),
});
